import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { finalize, Observable } from 'rxjs';
import { HolidayService } from '../../../core/services/holiday.service';
import { CustomToggleComponent } from '../../roteirizacao/components/custom-toggle/custom-toggle.component';
import { showAlertDialog } from '../../../shared/helpers/showAlertDialog';
import { AlertType } from '../../../shared/models/alertType';
import { HolidayMunicipalityOption, HolidayType, UpsertHolidayRequest } from '../../../shared/models/holiday';
import { NgSelectComponent } from '@ng-select/ng-select';

interface HolidayFormDialogData {
  mode: 'create' | 'edit';
  holidayId?: number;
}

@Component({
  selector: 'app-holiday-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CustomToggleComponent,
    NgSelectComponent,
  ],
  templateUrl: './holiday-form-dialog.component.html',
  styleUrl: './holiday-form-dialog.component.scss',
})
export class HolidayFormDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly holidayTypes: HolidayType[] = ['Nacional', 'Estadual', 'Municipal'];

  readonly form = this.formBuilder.group({
    type: ['Nacional' as HolidayType, [Validators.required]],
    state: [null as string | null],
    cityIbgeCode: [null as number | null],
    date: [null as Date | null, [Validators.required]],
    reason: ['', [Validators.required, this.nonWhitespaceValidator]],
    isRecurring: [true],
  });

  states: string[] = [];
  municipalities: HolidayMunicipalityOption[] = [];
  isLoading = false;
  isSubmitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: HolidayFormDialogData,
    private readonly dialogRef: MatDialogRef<HolidayFormDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly holidayService: HolidayService,
    private readonly dialog: MatDialog,
  ) {
    this.dialogRef.updateSize('520px');
  }

  ngOnInit(): void {
    this.setupTypeWatcher();
    this.setupStateWatcher();
    this.loadInitialData();
  }

  get title(): string {
    return this.data.mode === 'edit' ? 'Atualizar Feriado' : 'Novo Feriado';
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.buildPayload();
    this.isSubmitting = true;

    const request$: Observable<unknown> = this.data.mode === 'edit' && this.data.holidayId
      ? this.holidayService.update(this.data.holidayId, payload)
      : this.holidayService.create(payload);

    request$.pipe(finalize(() => (this.isSubmitting = false))).subscribe({
      next: () => {
        const message = this.data.mode === 'edit'
          ? 'Feriado atualizado com sucesso.'
          : 'Feriado cadastrado com sucesso.';

        showAlertDialog(this.dialog, AlertType.Success, 'Sucesso!', message)
          .afterClosed()
          .subscribe(() => {
            this.dialogRef.close({ updated: true });
          });
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 409) {
          showAlertDialog(
            this.dialog,
            AlertType.Error,
            'Duplicidade de feriado',
            'Já existe um feriado com a mesma data, tipo e abrangência geográfica.',
          );
          return;
        }

        showAlertDialog(
          this.dialog,
          AlertType.Error,
          'Erro',
          'Não foi possível salvar o feriado. Tente novamente.',
        );
      },
    });
  }

  private loadInitialData(): void {
    this.isLoading = true;

    this.holidayService
      .getFilterOptions()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (options) => {
          this.states = [...options.states].sort((a, b) => a.localeCompare(b));

          if (this.data.mode === 'edit' && this.data.holidayId) {
            this.loadHolidayForEdit(this.data.holidayId);
            return;
          }

          this.applyTypeRules(this.form.controls.type.value ?? 'Nacional');
        },
        error: () => {
          showAlertDialog(this.dialog, AlertType.Error, 'Erro', 'Não foi possível carregar os dados do formulário.');
        },
      });
  }

  private loadHolidayForEdit(holidayId: number): void {
    this.isLoading = true;

    this.holidayService
      .getById(holidayId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (holiday) => {
          this.form.patchValue(
            {
              type: holiday.type,
              state: holiday.state,
              date: this.parseApiDate(holiday.date),
              reason: holiday.reason,
              isRecurring: holiday.isRecurring,
            },
            { emitEvent: false },
          );

          this.applyTypeRules(holiday.type);

          if (holiday.type === 'Municipal' && holiday.state) {
            this.loadMunicipalities([holiday.state], holiday.cityIbgeCode);
          }
        },
        error: () => {
          showAlertDialog(this.dialog, AlertType.Error, 'Erro', 'Não foi possível carregar o feriado para edição.');
          this.dialogRef.close();
        },
      });
  }

  private setupTypeWatcher(): void {
    this.form.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type) => {
      this.applyTypeRules(type ?? 'Nacional');
    });
  }

  private setupStateWatcher(): void {
    this.form.controls.state.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      const selectedType = this.form.controls.type.value;

      if (selectedType !== 'Municipal') {
        return;
      }

      this.form.controls.cityIbgeCode.setValue(null, { emitEvent: false });

      if (!state) {
        this.municipalities = [];
        this.form.controls.cityIbgeCode.disable({ emitEvent: false });
        this.form.controls.cityIbgeCode.clearValidators();
        this.form.controls.cityIbgeCode.updateValueAndValidity({ emitEvent: false });
        return;
      }

      this.loadMunicipalities([state]);
    });
  }

  private applyTypeRules(type: HolidayType): void {
    const stateControl = this.form.controls.state;
    const cityControl = this.form.controls.cityIbgeCode;

    if (type === 'Nacional') {
      stateControl.setValue(null, { emitEvent: false });
      stateControl.disable({ emitEvent: false });
      stateControl.clearValidators();

      cityControl.setValue(null, { emitEvent: false });
      cityControl.disable({ emitEvent: false });
      cityControl.clearValidators();

      this.municipalities = [];
    }

    if (type === 'Estadual') {
      stateControl.enable({ emitEvent: false });
      stateControl.setValidators([Validators.required]);

      cityControl.setValue(null, { emitEvent: false });
      cityControl.disable({ emitEvent: false });
      cityControl.clearValidators();

      this.municipalities = [];
    }

    if (type === 'Municipal') {
      stateControl.enable({ emitEvent: false });
      stateControl.setValidators([Validators.required]);

      const currentState = stateControl.value;
      if (currentState) {
        cityControl.enable({ emitEvent: false });
        cityControl.setValidators([Validators.required]);
        this.loadMunicipalities([currentState]);
      } else {
        cityControl.setValue(null, { emitEvent: false });
        cityControl.disable({ emitEvent: false });
        cityControl.clearValidators();
        this.municipalities = [];
      }
    }

    stateControl.updateValueAndValidity({ emitEvent: false });
    cityControl.updateValueAndValidity({ emitEvent: false });
  }

  private loadMunicipalities(states: string[], preselectedCityIbgeCode?: number | null): void {
    this.holidayService.getFilterOptions(states).subscribe({
      next: (options) => {
        this.municipalities = [...options.municipalities].sort((a, b) => a.name.localeCompare(b.name));

        const cityControl = this.form.controls.cityIbgeCode;
        cityControl.enable({ emitEvent: false });
        cityControl.setValidators([Validators.required]);
        cityControl.setErrors(null);
        cityControl.updateValueAndValidity({ emitEvent: false });

        if (preselectedCityIbgeCode) {
          cityControl.setValue(preselectedCityIbgeCode, { emitEvent: false });
        }
      },
      error: () => {
        this.municipalities = [];

        const cityControl = this.form.controls.cityIbgeCode;
        cityControl.setValue(null, { emitEvent: false });
        cityControl.enable({ emitEvent: false });
        cityControl.setValidators([Validators.required]);
        cityControl.setErrors({ loadFailed: true });
        cityControl.markAsTouched();

        showAlertDialog(
          this.dialog,
          AlertType.Error,
          'Erro',
          'Não foi possível carregar os municípios do estado selecionado.',
        );
      },
    });
  }

  private nonWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const value = String(control.value ?? '').trim();
    return value.length > 0 ? null : { requiredTrimmed: true };
  }

  private buildPayload(): UpsertHolidayRequest {
    const type = this.form.controls.type.value ?? 'Nacional';
    const state = this.form.controls.state.value;
    const cityIbgeCode = this.form.controls.cityIbgeCode.value;

    return {
      type,
      date: this.formatDateForApi(this.form.controls.date.value),
      reason: this.form.controls.reason.value?.trim() ?? '',
      isRecurring: this.form.controls.isRecurring.value ?? false,
      state: type === 'Nacional' ? null : state,
      cityIbgeCode: type === 'Municipal' ? cityIbgeCode : null,
    };
  }

  private formatDateForApi(date: Date | null): string {
    if (!date) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseApiDate(date: string): Date | null {
    if (!date) {
      return null;
    }

    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }
}
